import React, { FC, ReactNode } from "react";
import { ArrowRightIcon } from "./icons";
import { useLocale } from "../i18n/locale";

export interface StepFooterProps {
  /** Omitted on the first step, which has nothing to go back to. */
  onBack?: () => void;
  /** Names the previous step instead of the generic "Back" where the design
   *  calls for it — step 3 reads "← Identity". */
  backLabel?: ReactNode;
  /** Omitted on the last step, which has nothing to advance to. */
  onContinue?: () => void;
  /** Empty label hides the primary button — same case as `onContinue`. */
  label?: string;
  /** Steps that gate on an upload or a signature keep it visible but inert. */
  disabled?: boolean;
  /**
   * True while the step's own work is running — a signature saving, a file
   * uploading. It disables the button too, but reads as "in progress" rather
   * than "not allowed", which says something quite different to the tenant.
   */
  busy?: boolean;
  className?: string;
}

/**
 * The rule-and-buttons row that closes every centre panel: a quiet text link
 * back on the left, the primary action on the right.
 *
 * On a phone it sticks to the bottom of the viewport, so the way forward stays
 * reachable on the long steps without scrolling to the end.
 */
const StepFooter: FC<StepFooterProps> = ({
  onBack,
  backLabel,
  onContinue,
  label,
  disabled = false,
  busy = false,
  className = "",
}) => {
  const { t } = useLocale();
  const showPrimary = Boolean(label) && Boolean(onContinue);

  return (
    <div
      className={`sticky bottom-0 mt-0.5 flex items-center justify-between border-t border-line bg-white pb-0.5 pt-4 min-[760px]:static min-[760px]:pb-0 min-[760px]:pt-[21px] ${className}`}
    >
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="py-3 text-[13px] text-[#7e8993] transition-colors hover:text-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2"
        >
          ← {backLabel || t.desktop.back}
        </button>
      ) : (
        // Keeps the primary button pinned right on the first step.
        <span aria-hidden="true" />
      )}

      {showPrimary && (
        <button
          type="button"
          onClick={onContinue}
          disabled={disabled || busy}
          aria-busy={busy}
          className={`flex min-w-[205px] items-center justify-between gap-4 rounded-[4px] bg-navy px-[22px] py-[12px] text-[12px] font-extrabold tracking-[0.04em] text-white transition-colors hover:bg-navy-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2 disabled:bg-[#c9c9c5] disabled:text-[#f1f1ee] min-[760px]:min-w-[230px] min-[760px]:text-[13px] ${
            busy ? "cursor-wait" : "disabled:cursor-not-allowed"
          }`}
        >
          {label}
          <ArrowRightIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

export default StepFooter;
